export type ModuleKey = 'monitoring' | 'analytics' | 'simulations' | 'project';

/**
 * Commercial launch state of a module — independent of any project's entitlement.
 * - comingSoon : Not released. No purchase form; only a lightweight waitlist signup.
 * - beta       : Released to a small cohort / public testing. Purchasable, with a "Beta" pill.
 * - available  : Fully launched and generally purchasable.
 */
export type ModuleLaunchStatus = 'comingSoon' | 'beta' | 'available';

/**
 * SOURCE OF TRUTH for module launch state across the app (dashboard tiles,
 * sidebar, purchase page). Flip on launch day and redeploy — this is a
 * deploy-time fact, not a per-project/tenant setting, so it lives in code
 * rather than the BillingConfig DB model.
 */
export const MODULE_LAUNCH_STATUS: Record<ModuleKey, ModuleLaunchStatus> = {
  monitoring: 'available',
  analytics: 'comingSoon',
  simulations: 'beta',
  project: 'available',
};

/**
 * FMS Node Definitions
 *
 * Two structural variants enforce business rules at compile time:
 *
 * FMSLeafNode — a terminal metric/feature (M.K with no children, or M.K.D).
 *   - May be isSku (billable unit that appears on invoices).
 *   - Cannot be isAbstract (only parent nodes can act as shared pools).
 *
 * FMSParentNode — an M.K node that has children.
 *   - May be isAbstract (shared consumption pool across children).
 *   - May be isMetric (valid aggregation axis on usage dashboard).
 *   - Cannot be isSku (only leaf nodes can be billed directly).
 *   - When isAbstract, a manually created bucket here caps all children.
 *     Parent buckets are never auto-provisioned — operators create them explicitly.
 *
 * FMSParamNode — a scalar bound on an operation input (e.g. max duration hours).
 *   - NOT counted over time. Checked inline against user input.
 *   - Mutually exclusive with isFeature / isMetric / isSku / isAbstract.
 */
export interface FMSLeafNode {
  isFeature?: boolean;
  isMetric?: boolean;
  isSku?: boolean;
  isAbstract?: never; // leaf nodes cannot be abstract pools
  isParam?: boolean;
  description?: string;
}

export interface FMSParentNode {
  isFeature?: boolean;
  isMetric?: boolean; // allowed: marks this path as a valid aggregation axis on the usage dashboard
  isSku?: never;      // parent nodes cannot be SKUs — only leaves can be billed directly
  isAbstract?: boolean;
  description?: string;
}

export interface FMSParamNode {
  isParam: true;
  description?: string;
}

/** Legacy alias kept for runtime code that still references FMSNode generically. */
export type FMSNode = FMSLeafNode | FMSParentNode | FMSParamNode;

export interface FMSTree {
  [key: string]: FMSNode | { [key: string]: FMSNode | { [key: string]: FMSNode } };
}

/**
 * SOURCE OF TRUTH: Global FMS Hierarchy
 */
export const FMS_DEFINITIONS = {
  monitoring: {
    create_measurement: {
      isFeature: true,
      isAbstract: true,
      maturity: { isFeature: false, isMetric: true, isSku: true },
      temperature: { isFeature: false, isMetric: true, isSku: true },
      UPV: { isFeature: false, isMetric: true, isSku: true },
      mota: { isFeature: false, isMetric: true, isSku: true },
    },
    device_rental_day: {
      // cosmos
      CTM: { isMetric: true, isSku: true },
      GW_7240: { isMetric: true, isSku: true },
      GW_7268: { isMetric: true, isSku: true },
      GW_lite: { isMetric: true, isSku: true },
      // partner - UPV
      SMART_SENSOR: { isMetric: true, isSku: false },
      MINI_PC: { isMetric: true, isSku: false },
      // auxiliary
      repeater_set: { isMetric: true, isSku: true },
      PSU: { isMetric: true, isSku: true },
    },
    ingest_datapoint: {
      isAbstract: true,
      maturity: { isFeature: false, isMetric: true, isSku: false },
      temperature: { isFeature: false, isMetric: true, isSku: false },
      UPV: { isFeature: false, isMetric: true, isSku: false },
      mota: { isFeature: false, isMetric: true, isSku: false },
    },
    reports: {
      isFeature: true,
      generate: { isFeature: true, isMetric: true, isSku: false },
      download: { isFeature: true, isMetric: true, isSku: false },
      send_by_email: { isFeature: true, isMetric: true, isSku: false },
    },
    device: {
      registered: { isMetric: true },
    },
    charts: {
      isFeature: true,
      strength_prediction: { isFeature: true, isMetric: false, isSku: false },
    },
    summarize: {
      isFeature: true,
      advanced_options: { isFeature: true, isMetric: false, isSku: false },
    },
  },
  analytics: {
    create_pour: { isFeature: true, isMetric: true, isSku: true },
    create_strength_test_result: { isFeature: true, isMetric: true, isSku: true },
    create_lot: { isFeature: true, isMetric: true, isSku: false },
    ocr_job: {
      isAbstract: true,
      isFeature: true,
      isMetric: true,
      strengt_test_results: { isFeature: true, isMetric: true, isSku: true },
      pour: { isFeature: true, isMetric: true, isSku: true },
    }
  },
  simulations: {
    run_simulation: {
      isAbstract: true,
      isFeature: true,
      isMetric: true,
      '1D': { isFeature: true, isMetric: true, isSku: false },
      '2D': { isFeature: true, isMetric: true, isSku: false },
      '3D': { isFeature: true, isMetric: true, isSku: true },
    },
    viewers: {
      '3D': { isFeature: true },
    },
    options: {
      weather_API: { isFeature: true, isMetric: true, isSku: true },
      custom_mixtures: { isFeature: true, isMetric: true, isSku: true },
    },
    export: {
      isFeature: true,
      isMetric: true,
      xlsx: { isFeature: true, isMetric: true, isSku: false },
      pdf: { isFeature: true, isMetric: true, isSku: false },
      full_data_grids: { isFeature: true, isMetric: false, isSku: false },
    },
    params: {
      simulation_duration_hours: { isParam: true, description: 'Max simulation time horizon in hours per run' },
      max_saved_simulations: { isParam: true, description: 'Max number of favourited (permanently retained) simulations' },
    },
  },
  project: {
    weather: {
      forecast: { isFeature: true, isMetric: true, isSku: false },
    },
    cosmos_ai: {
      concreting_recommendations: { isFeature: true, isMetric: true, isSku: false },
    },
    // LIMITE -> tiene que llevar un PARAM asociado a un tier
    access: {
      total_users: { isFeature: true, isMetric: true, isSku: false },
    }
  },
  platform: {
    sign_up: {
      new_user: { isMetric: true },
      validation_sms_sent: { isMetric: true },
      validation_email_sent: { isMetric: true },
    },
    auth: {
      login_failure: { isMetric: true },
      token_refresh: { isMetric: true },
    }
  }
} as const;

/**
 * Helper to generate type-safe FMS IDs from the tree
 */
type FMSNodeKeys = 'isFeature' | 'isMetric' | 'isSku' | 'isAbstract' | 'description';

type Join<K, P> = K extends string | number ?
  P extends string | number ?
  `${K}${"" extends P ? "" : "."}${P}`
  : never : never;

type Prev = [never, 0, 1, 2, 3, ...any[]];

type Paths<T, D extends number = 3> = [D] extends [never] ? never : T extends object ?
  { [K in keyof T]-?: K extends string | number ?
    K extends FMSNodeKeys ? never :
    T[K] extends object ?
      K | Join<K, Paths<T[K], Prev[D]>>
      : K
    : never
  }[keyof T] : never;

/**
 * Union of all valid hierarchical identifiers (e.g. 'monitoring.create_measurement.maturity')
 */
export type FMS_ID = Paths<typeof FMS_DEFINITIONS>;

/**
 * Resolves the value type at a dotted path P within tree T (up to 3 levels).
 * Used to inspect node flags at a given FMS path.
 */
type NodeAt<T, P extends string> =
  P extends `${infer A}.${infer B}.${infer C}`
    ? A extends keyof T ? B extends keyof T[A] ? C extends keyof T[A][B] ? T[A][B][C] : never : never : never
    : P extends `${infer A}.${infer B}`
      ? A extends keyof T ? B extends keyof T[A] ? T[A][B] : never : never
      : P extends keyof T ? T[P] : never;

/**
 * Subset of FMS_ID paths where the resolved node has isFeature: true.
 * Derived automatically from FMS_DEFINITIONS — no manual maintenance required.
 */
export type FEATURE_ID = {
  [P in FMS_ID]: NodeAt<typeof FMS_DEFINITIONS, P> extends { isFeature: true } ? P : never
}[FMS_ID];

/**
 * Subset of FMS_ID paths where the resolved node has isMetric: true.
 * These are tracked in metering_events and may have quota buckets.
 */
export type METRIC_ID = {
  [P in FMS_ID]: NodeAt<typeof FMS_DEFINITIONS, P> extends { isMetric: true } ? P : never
}[FMS_ID];

/**
 * Subset of FMS_ID paths where the resolved node has isSku: true.
 * These appear in invoices and carry pricing via a linked SKU.
 */
export type BILLABLE_SKU_ID = {
  [P in FMS_ID]: NodeAt<typeof FMS_DEFINITIONS, P> extends { isSku: true } ? P : never
}[FMS_ID];

/**
 * Subset of FMS_ID paths where the resolved node has isParam: true.
 * These are scalar bounds on operation inputs — not counted, not binary.
 * Populated into ModuleEntitlement.params by hydrateEntitlements.
 */
export type PARAM_ID = {
  [P in FMS_ID]: NodeAt<typeof FMS_DEFINITIONS, P> extends { isParam: true } ? P : never
}[FMS_ID];

// ─── Module Tiers ───────────────────────────────────────────────────────────

export interface TierBucket {
  metric: METRIC_ID;
  resetTarget: number | null; // monthly refill target; null = one-time package
  overuseLimit: number;
  isBillable?: boolean;
  allowNegative?: boolean;
}

export interface TierDefinition {
  name: string;
  /** Feature flags enabled for this tier. */
  features: FEATURE_ID[];
  /**
   * Metering buckets to initialise when scaffolding this tier.
   * hydrateEntitlements creates these if no buckets are already set.
   */
  buckets?: TierBucket[];
  /**
   * Scalar bounds on operation inputs for this tier.
   * Keys are PARAM_ID strings; values are the numeric limit.
   * hydrateEntitlements copies these into ModuleEntitlement.params if params is empty.
   */
  params?: Partial<Record<PARAM_ID, number>>;
}

export const MODULE_TIERS: Record<ModuleKey, Record<string, TierDefinition>> = {
  monitoring: {
    free: {
      name: 'Free',
      features: ['monitoring.create_measurement','monitoring.reports.generate'],
    },
    standard: {
      name: 'Standard',
      features: ['monitoring.create_measurement', 'monitoring.reports.generate'],
    },
    pro: {
      name: 'Pro',
      features: ['monitoring.create_measurement', 'monitoring.reports.generate'],
    },
  },
  analytics: {
    free: {
      name: 'Free',
      features: ['analytics.create_pour'],
    },
    pro: {
      name: 'Pro',
      features: ['analytics.create_pour', 'analytics.ocr_job'],
    },
  },
  simulations: {
    free: {
      name: 'Free',
      features: [
        'simulations.run_simulation', 'simulations.run_simulation.1D', 'simulations.run_simulation.2D',
        'simulations.export', 'simulations.export.xlsx', 'simulations.export.pdf',
      ],
      buckets: [
        // Monthly cap: 20 single-element (1D) simulations per cycle.
        // { metric: 'simulations.run_simulation', resetTarget: 20, overuseLimit: 0 },
      ],
      params: {
        'simulations.params.simulation_duration_hours': 168, // 7 days
        'simulations.params.max_saved_simulations': 5,
      },
    },
    pro: {
      name: 'Pro',
      features: [
        'simulations.run_simulation', 'simulations.run_simulation.1D', 'simulations.run_simulation.2D', 'simulations.run_simulation.3D',
        'simulations.viewers.3D', 'simulations.options.custom_mixtures', 'simulations.options.weather_API',
        'simulations.export', 'simulations.export.xlsx', 'simulations.export.pdf', 'simulations.export.full_data_grids',
      ],
      buckets: [],     // unlimited — no cap bucket needed
      params: {
        'simulations.params.simulation_duration_hours': 336, // 14 days
        'simulations.params.max_saved_simulations': 50,
      },
    },
  },
  project: {
    free: {
      name: 'Free',
      features: ['project.weather.forecast'],
    },
    pro: {
      name: 'Pro',
      features: ['project.weather.forecast', 'project.cosmos_ai.concreting_recommendations'],
    },
  },
  // platform: {
  //   free: {
  //     name: 'Free',
  //     features: [], // Global platform features don't typically follow project tiers
  //   },
  // },
};

// ─── Billing Config Template well-known keys ───────────────────────────────
// See docs/superpowers/specs/2026-09-15-billing-config-templates-design.md.
// These keys must match the `key` field of the corresponding seeded
// BillingConfigTemplate documents (mongo/migrations.mongo.js).

export const SELF_SERVE_TEMPLATE_KEY = 'self-serve-simulator';
export const DEFAULT_STAFF_TEMPLATE_KEY = 'default-staff-provisioned';
